import classNames from 'classnames';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ISpecialPackageDto } from '../model';
import { PriceRender, StyledButton } from '../ui';
import SpecialPackageOccupancyPicker from './occupancy-picker';
import {
  calculateSpecialPackageDeposit,
  getAvailableSpecialPackageOccupancies,
  getDefaultSpecialPackageOccupancy,
  getSpecialPackagePriceForOccupancy,
  getSpecialPackageRoomPriceForOccupancy,
  isSpecialPackageRoomSelectable,
  SpecialPackageOccupancy,
} from './occupancy';
import {
  getSpecialPackageDescription,
  getSpecialPackageTitle,
} from './text';
import { ISpecialPackageReferenceData } from './use-special-package-references';

export interface ISpecialPackageCardProps {
  specialPackage: ISpecialPackageDto;
  references?: ISpecialPackageReferenceData;
  selectedRoomCategoryId?: number | null;
  selectedOccupancy?: number | null;
  onOccupancyChange?: (occupancy: SpecialPackageOccupancy) => void;
  onSelect?: () => void;
  actionLabel?: string;
}

export function SpecialPackageCard(props: ISpecialPackageCardProps) {
  const { t, i18n } = useTranslation('offer-package');
  const { specialPackage, references } = props;
  const availableOccupancies =
    getAvailableSpecialPackageOccupancies(specialPackage);
  const selectedOccupancy = availableOccupancies.includes(
    props.selectedOccupancy as SpecialPackageOccupancy
  )
    ? (props.selectedOccupancy as SpecialPackageOccupancy)
    : getDefaultSpecialPackageOccupancy(specialPackage);
  const packagePrice = getSpecialPackagePriceForOccupancy(
    specialPackage,
    selectedOccupancy
  );
  const packageTitle = getSpecialPackageTitle(specialPackage, i18n.language);
  const packageDescription = getSpecialPackageDescription(
    specialPackage,
    i18n.language
  );
  const roomCategories = specialPackage.specialPackageRoomCategoryList.filter(
    (room) => isSpecialPackageRoomSelectable(room, selectedOccupancy)
  );

  const renderOriginalPrice = (price: number | null) => {
    if (price === null) {
      return null;
    }

    return (
      <div className="text-right">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          {t('special.shared.original-price')}
        </p>
        <div className="font-semibold text-slate-900">
          <PriceRender price={price}></PriceRender>
        </div>
      </div>
    );
  };

  const getRoomTitle = (id: number | null) => {
    if (id === null) {
      return t('special.shared.room');
    }

    const roomCategory = references?.roomCategoryById[id];

    if (!roomCategory) {
      return `${t('special.shared.room')} #${id}`;
    }

    return (
      roomCategory.roomCategoryTranslationList.find(
        (translation) => translation.langCode === i18n.language
      )?.title ?? roomCategory.internalName
    );
  };

  const getCureTitle = (id: number) => {
    const cure = references?.cureById[id];

    if (!cure) {
      return `${t('special.shared.cure')} #${id}`;
    }

    return (
      cure.cureTranslationList.find(
        (translation) => translation.langCode === i18n.language
      )?.title ?? cure.internalName
    );
  };

  const getProductTitle = (id: number) => {
    const product = references?.productById[id];

    if (!product) {
      return `${t('special.shared.product')} #${id}`;
    }

    return (
      product.productTranslationList.find(
        (translation) => translation.langCode === i18n.language
      )?.title ?? product.internalName
    );
  };

  const getTreatmentTitle = (id: number) => {
    const treatment = references?.treatmentById[id];

    if (!treatment) {
      return `${t('special.shared.treatment')} #${id}`;
    }

    return (
      treatment.treatmentTranslationList.find(
        (translation) => translation.langCode === i18n.language
      )?.title ?? treatment.internalName
    );
  };

  return (
    <div className="border-4 border-brand-green-700 bg-white overflow-clip shadow-md">
      <div className="bg-brand-green-700 text-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <p className="text-sm uppercase tracking-[0.2em]">
              {t('special.detail.eyebrow')}
            </p>
            <h1 className="text-2xl font-serif">{packageTitle}</h1>
          </div>

          <div className="min-w-40 text-right">
            <p className="text-sm">{t('special.shared.reduced-total')}</p>
            <p className="text-2xl font-semibold">
              {packagePrice === null ? (
                t('special.shared.price-on-request')
              ) : (
                <PriceRender price={packagePrice}></PriceRender>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6 p-4">
        {packageDescription && (
          <p className="whitespace-pre-line text-brand-cashmere-800">
            {packageDescription}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              {t('special.shared.arrival')}
            </p>
            <p className="mt-2 text-lg text-slate-900">
              {format(specialPackage.arrival, 'dd.MM.yyyy')}
            </p>
          </div>

          <div className="rounded-lg bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              {t('special.shared.departure')}
            </p>
            <p className="mt-2 text-lg text-slate-900">
              {format(specialPackage.departure, 'dd.MM.yyyy')}
            </p>
          </div>

          <div className="rounded-lg bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              {t('special.shared.occupancy')}
            </p>
            <div className="mt-2">
              {props.onOccupancyChange && availableOccupancies.length > 1 ? (
                <SpecialPackageOccupancyPicker
                  availableOccupancies={availableOccupancies}
                  selectedOccupancy={selectedOccupancy}
                  onChange={props.onOccupancyChange}
                ></SpecialPackageOccupancyPicker>
              ) : (
                <p className="text-lg text-slate-900">
                  {t(
                    selectedOccupancy === 1
                      ? 'special.shared.one-person'
                      : 'special.shared.two-persons'
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-brand-cashmere-900">
              {t('special.shared.rooms')}
            </h2>
            {references?.isLoading && (
              <span className="text-sm text-slate-500">
                {t('special.shared.loading')}
              </span>
            )}
          </div>

          <div className="grid gap-3">
            {roomCategories.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                {t('special.booking.no-rooms-for-occupancy')}
              </div>
            )}

            {roomCategories.map((room) => (
              <div
                key={room.id}
                className={classNames(
                  'rounded-lg border p-4',
                  props.selectedRoomCategoryId === room.id
                    ? 'border-brand-green-700 bg-brand-green-50'
                    : 'border-slate-200 bg-white'
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="font-semibold text-slate-900">
                      {getRoomTitle(room.roomCategoryId)}
                    </p>
                  </div>

                  {renderOriginalPrice(
                    getSpecialPackageRoomPriceForOccupancy(
                      room,
                      selectedOccupancy
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {specialPackage.specialPackageCureList.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-lg font-semibold text-brand-cashmere-900">
              {t('cure.title')}
            </h2>

            <ul className="space-y-2">
              {specialPackage.specialPackageCureList.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-4 rounded-lg bg-slate-50 px-4 py-3"
                >
                  <span className="text-slate-900">
                    {selectedOccupancy}x {getCureTitle(item.cureId)}
                  </span>
                  {renderOriginalPrice(
                    item.price == null ? null : item.price * selectedOccupancy
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {specialPackage.specialPackageTreatmentList.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-lg font-semibold text-brand-cashmere-900">
              {t('treatment.title')}
            </h2>

            <ul className="space-y-2">
              {specialPackage.specialPackageTreatmentList.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-4 rounded-lg bg-slate-50 px-4 py-3"
                >
                  <span className="text-slate-900">
                    {item.amount}x {getTreatmentTitle(item.treatmentId)}
                  </span>
                  {renderOriginalPrice(item.price)}
                </li>
              ))}
            </ul>
          </section>
        )}

        {specialPackage.specialPackageProductList.length > 0 && (
          <section className="space-y-2">
            <h2 className="text-lg font-semibold text-brand-cashmere-900">
              {t('product.title')}
            </h2>

            <ul className="space-y-2">
              {specialPackage.specialPackageProductList.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-4 rounded-lg bg-slate-50 px-4 py-3"
                >
                  <span className="text-slate-900">
                    {item.amount}x {getProductTitle(item.productId)}
                  </span>
                  {renderOriginalPrice(item.price)}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="space-y-3 rounded-xl bg-brand-cashmere-50 p-4">
          <div className="flex items-center justify-between gap-4">
            <span className="font-semibold text-slate-900">
              {t('special.shared.reduced-total')}
            </span>
            {packagePrice === null ? (
              <span>{t('special.shared.price-on-request')}</span>
            ) : (
              <PriceRender price={packagePrice}></PriceRender>
            )}
          </div>

          <div className="flex items-center justify-between gap-4">
            <span className="font-semibold text-slate-900">
              {t('shared.deposit')}
            </span>
            {calculateSpecialPackageDeposit(packagePrice) === null ? (
              <span>{t('special.shared.price-on-request')}</span>
            ) : (
              <PriceRender
                price={calculateSpecialPackageDeposit(packagePrice) ?? 0}
              ></PriceRender>
            )}
          </div>
        </div>
      </div>

      {props.onSelect && (
        <div className="bg-slate-100 p-4 print:hidden">
          <div className="flex justify-end">
            <StyledButton
              type="button"
              design="primary"
              className="outline-2 outline-solid outline-brand-green-700 outline-offset-2 animate-pulse-outline"
              onClick={props.onSelect}
            >
              {props.actionLabel ?? t('shared.book')}
            </StyledButton>
          </div>
        </div>
      )}
    </div>
  );
}

export default SpecialPackageCard;
